import { useLazyQuery } from "@apollo/client"
import moment from "moment"
import { useCallback, useContext, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { TEMP_BILLING_RECONCILIATION } from "../../constants"
import { AppContext } from "../../context"
import {
  COLUMN_INTERFACE_TYPES_FOR_TEMP_BILLING_RECONCILIATION,
  ROLES,
  TEMP_BILLING_RECONCILIATION_ROUTE,
} from "../../utils/constant"
import { moneyFormatter } from "../../utils/helper"
import Loader from "../layout/Loader"
import Pagination from "../layout/Pagination"
import { ITempBillingPayment, ITempBillingPaymentTotals, TEMP_BILLING_PAYMENTS } from "./gql"
import "./list-page.scss"
import PaidInvoicesModal from "./PaidInvoicesModal"
import StampPaymentModal from "./StampPaymentModal"

// kan-101. One row per Recruiter Profit Summary import — each import IS one
// People 2.0 payment. The executive stamps it with the ACH received date and a
// reference id, then reconciles the invoices it covers on the detail view.
//
// The layout follows the design handoff: filters live in the page toolbar (not
// the table header), paired fields collapse into two-line cells, and the summary
// strip is fed by SERVER-side totals over the whole filtered set — a page-local
// sum would misreport the moment the executive paginates. Every colour comes
// from a `--tbr-*` custom property in list-page.scss so the page follows the
// app's dark-mode toggle.
//
// kan-112: an invoice is SETTLED once it is cleared, accepted or put in
// suspense, and every count here is of settled invoices. Suspense items no
// longer block a payment, so they are surfaced instead — a note on the row and
// a card on the strip — because each one still owes an investigation.

let time = 0

/** Rows-per-page choices, per the handoff footer. */
const ROWS_PER_PAGE = [10, 25, 50]

/** Mirrors the server's TempBillingPaymentStatusFilter enum. */
type TempBillingPaymentStatus = 'OPEN' | 'CLOSED' | 'ALL'

const PAYMENT_STATUS_OPTIONS: Array<{ value: TempBillingPaymentStatus; label: string }> = [
  { value: 'OPEN', label: 'Only payments still open' },
  { value: 'CLOSED', label: 'Only payments fully closed' },
  { value: 'ALL', label: 'All payments' },
]

/** The first summary card counts the FILTERED set, so its label follows the filter. */
const PAYMENT_COUNT_LABEL: Record<TempBillingPaymentStatus, string> = {
  OPEN: 'Open Payments',
  CLOSED: 'Closed Payments',
  ALL: 'All Payments',
}

/**
 * Sortable columns. The key is the sort column name the resolver understands —
 * `invoices` (done/total ratio) and `reconStatus` (orphan count) are DERIVED
 * there rather than SQL columns, but the client sends them the same way.
 * `Actions` is inert, matching the handoff.
 */
const COLUMNS: Array<{ key: string | null; label: string }> = [
  { key: 'paymentReferenceId', label: 'Payment Ref' },
  { key: 'paymentReceivedDate', label: 'Received' },
  { key: 'fileName', label: 'File / Amount' },
  { key: 'monthYr', label: 'Month' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'reconStatus', label: 'Recon Status' },
  { key: 'activityReportMonth', label: 'Activity' },
  { key: null, label: 'Actions' },
]

const EMPTY_TOTALS: ITempBillingPaymentTotals = {
  stampedCount: 0,
  unreconciledAmount: 0,
  clearedInvoices: 0,
  settledInvoices: 0,
  acceptedInvoices: 0,
  suspenseInvoices: 0,
  suspenseAmount: 0,
  suspenseDifference: 0,
  filesWithSuspense: 0,
  totalInvoices: 0,
  orphanCount: 0,
  filesWithOrphans: 0,
}

/**
 * Where a payment stands, as one pill. `stamped` gates reconciliation entirely,
 * so it comes first. `orphanCount` is UNRESOLVED orphans only — an accepted or
 * suspense orphan is a decision and no longer blocks.
 */
const statusPill = (payment: ITempBillingPayment): { tone: string; label: string } => {
  if (!payment?.stamped) return { tone: 'is-neutral', label: 'Not Stamped' }
  if (payment?.fullyReconciled) return { tone: 'is-active', label: 'Reconciled' }
  if (payment?.orphanCount) return { tone: 'is-danger', label: 'Orphans' }
  return { tone: 'is-warn', label: 'Open' }
}

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="11" cy="11" r="6.5" />
    <path d="M16 16l4.5 4.5" />
  </svg>
)

const WarningIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 4l9 16H3z" />
    <path d="M12 10v4M12 17v.1" />
  </svg>
)

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="5" y="11" width="14" height="9" rx="1.5" />
    <path d="M8 11V8a4 4 0 018 0v3" />
  </svg>
)

const AllTempBillingReconciliations = () => {
  const { userRoles } = useContext(AppContext)
  const isExecutive = !!userRoles?.includes(ROLES.EXECUTIVE)

  const defaultColumnFilters: COLUMN_INTERFACE_TYPES_FOR_TEMP_BILLING_RECONCILIATION = {
    referenceIdSearch: '',
    // The handoff's default view: newest month first.
    sorting: {
      columnName: 'monthYr',
      columnValue: 'DESC'
    }
  };

  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtering, setFiltering] = useState<boolean>(false)
  // Three states, not a checkbox: the executive's working list is the open
  // payments, but they also need to review what has been closed out, and
  // occasionally see everything. A boolean could only ever say two of the three.
  const [status, setStatus] = useState<TempBillingPaymentStatus>('OPEN')
  const [columnFilters, setColumnFilters] =
    useState<COLUMN_INTERFACE_TYPES_FOR_TEMP_BILLING_RECONCILIATION>(defaultColumnFilters);
  const [stampPayment, setStampPayment] = useState<ITempBillingPayment | null>(null)
  // Held in state so the options do not blink away while the next response is in
  // flight — the payload sends the full list on every request.
  const [activityOptions, setActivityOptions] = useState<string[]>([])

  const [listPayments, { data: listData, loading: listLoading, refetch }] = useLazyQuery<{
    tempBillingPayments: {
      payments: ITempBillingPayment[],
      count: number,
      totals: ITempBillingPaymentTotals,
      availableActivityReports: string[],
    }
  }>(TEMP_BILLING_PAYMENTS, { fetchPolicy: 'network-only' });

  /** Typing debounces; every other control applies immediately. */
  const changeSearch = (value: string) => {
    setFiltering(true)
    setCurrentPage(1)
    setColumnFilters((prevFilters) => ({ ...prevFilters, referenceIdSearch: value }))
  }

  /**
   * "Which file carries this invoice?" The invoice may well sit on a payment
   * that is already closed, so typing a number widens the status filter to ALL
   * the same way picking an activity report does — otherwise the default OPEN
   * view answers "no such invoice" when it means "wrong filter". Clearing the
   * box leaves the status filter alone.
   */
  const changeInvoiceSearch = (value: string) => {
    setFiltering(true)
    setCurrentPage(1)
    if (value.trim()) setStatus('ALL')
    setColumnFilters((prevFilters) => ({ ...prevFilters, invoiceNumberSearch: value }))
  }

  /**
   * Every payment reported under one activity-report tag.
   *
   * Picking a tag also widens the status filter to ALL. A payment can only BE
   * tagged once it is fully reconciled, so under the default OPEN status the two
   * filters are mutually exclusive and the answer is always an empty table —
   * which reads as "no such activity report" rather than "wrong filter".
   * Clearing the tag leaves the status filter alone.
   */
  const changeActivity = (value: string) => {
    setFiltering(false)
    setCurrentPage(1)
    if (value) setStatus('ALL')
    setColumnFilters((prevFilters) => ({ ...prevFilters, activityReportSearch: value }))
  }

  /** A new column sorts ascending; the active column flips. */
  const changeSorting = (columnName: string) => {
    setFiltering(false)
    setCurrentPage(1)
    setColumnFilters((prevFilters) => {
      const active = prevFilters?.sorting?.columnName === columnName
      return {
        ...prevFilters,
        sorting: {
          columnName,
          columnValue: active && prevFilters?.sorting?.columnValue === 'ASC' ? 'DESC' : 'ASC',
        },
      }
    });
  };

  const buildFilters = useCallback(() => {
    // Only send what is actually set — an empty sorting object would otherwise
    // reach the resolver as a column named ''.
    const filters: Record<string, any> = { status }
    if (columnFilters?.referenceIdSearch) {
      filters.referenceIdSearch = columnFilters.referenceIdSearch
    }
    if (columnFilters?.activityReportSearch) {
      filters.activityReportSearch = columnFilters.activityReportSearch
    }
    if (columnFilters?.invoiceNumberSearch?.trim()) {
      filters.invoiceNumberSearch = columnFilters.invoiceNumberSearch.trim()
    }
    if (columnFilters?.sorting?.columnName) {
      filters.sorting = columnFilters.sorting
    }
    return filters
  }, [columnFilters, status])

  const listPaymentsCallback = useCallback(async () => {
    const variables = {
      payload: { limit, page: currentPage },
      filters: buildFilters(),
    }
    // Typing in the search box fires on every keystroke — debounce those, but
    // keep page/limit/sort/checkbox changes immediate.
    if (filtering) {
      window.clearTimeout(time);
      time = window.setTimeout(async () => {
        await listPayments({ variables });
      }, 200);
    } else {
      await listPayments({ variables });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, columnFilters, status]);

  useEffect(() => {
    listPaymentsCallback()
  }, [listPaymentsCallback])

  useEffect(() => {
    const reports = listData?.tempBillingPayments?.availableActivityReports
    if (!reports) return
    setActivityOptions((previous) => (
      previous.length === reports.length && previous.every((tag, index) => tag === reports[index])
        ? previous
        : reports
    ))
  }, [listData])

  const caretFor = (columnName: string | null) => {
    if (!columnName || columnFilters?.sorting?.columnName !== columnName) return ''
    return columnFilters?.sorting?.columnValue === 'ASC' ? '▲' : '▼'
  }

  const paymentRow = (payment: ITempBillingPayment) => {
    const {
      id, fileName, monthYr, paymentReceivedDate, paymentReferenceId, activityReportMonth,
      stamped, invoiceCount, settledCount, acceptedCount, suspenseCount, orphanCount,
      unkeyedRowCount, profitSummaryTotal, hasReconciliationRows,
    } = payment

    const { tone, label } = statusPill(payment)
    const percent = invoiceCount ? Math.round(((settledCount || 0) / invoiceCount) * 100) : 0

    return (
      <div className="tbr-row" key={id}>
        <div className="tbr-col">
          <span className="tbr-ref">{paymentReferenceId || '--'}</span>
        </div>

        <div className="tbr-col">
          <span className="tbr-received">
            {paymentReceivedDate ? moment(paymentReceivedDate).format('MM/DD/YYYY') : '--'}
          </span>
        </div>

        <div className="tbr-col tbr-file">
          <Link
            className="tbr-file-link"
            title={fileName || ''}
            to={`${TEMP_BILLING_RECONCILIATION_ROUTE}/${id}`}
          >
            {fileName || '--'}
          </Link>
          <span className="tbr-amount">{moneyFormatter(profitSummaryTotal || 0)}</span>
        </div>

        <div className="tbr-month">{monthYr ? moment(monthYr).format('MMM-YYYY') : '--'}</div>

        <div className="tbr-invoices">
          <div className="tbr-invoice-counts">
            <span className="tbr-invoice-done">{settledCount || 0}</span>
            <span className="tbr-invoice-total">of {invoiceCount} invoices</span>
          </div>
          <div className="tbr-meter">
            <div className="tbr-meter-fill" style={{ width: `${percent}%` }} />
          </div>
        </div>

        <div className="tbr-status">
          <span className={`tbr-pill ${tone}`}>{label}</span>
          {!!orphanCount && (
            <span className="tbr-note is-danger">
              <WarningIcon />
              <span>{orphanCount} orphan(s)</span>
            </span>
          )}
          {/* Suspense does not block the payment, so the pill can read
              Reconciled — the note is what keeps the open investigation from
              disappearing from view. */}
          {!!suspenseCount && (
            <span className="tbr-note is-warn">
              <WarningIcon />
              <span>{suspenseCount} in suspense</span>
            </span>
          )}
          {!!acceptedCount && (
            <span className="tbr-note">{acceptedCount} accepted</span>
          )}
          {/* The server refuses to delete an import once anything under it has
              been reconciled — say so here rather than letting the imports page
              surprise them. */}
          {!!hasReconciliationRows && (
            <span className="tbr-note">
              <LockIcon />
              <span>Import locked</span>
            </span>
          )}
          {!!unkeyedRowCount && (
            <span className="tbr-note">{unkeyedRowCount} row(s) with no invoice #</span>
          )}
        </div>

        <div className={`tbr-activity${activityReportMonth ? '' : ' is-empty'}`}>
          {activityReportMonth ? moment(activityReportMonth).format('MMM-YY') : '--'}
        </div>

        <div className="tbr-actions">
          <button
            type="button"
            className="tbr-btn is-outline"
            onClick={() => setStampPayment(payment)}
          >
            {stamped ? 'Edit Stamp' : 'Stamp'}
          </button>
          <Link className="tbr-btn is-ghost" to={`${TEMP_BILLING_RECONCILIATION_ROUTE}/${id}`}>
            Reconcile
          </Link>
        </div>
      </div>
    )
  }

  // Client-side gate is convenience only — every operation is guarded server-side.
  if (!isExecutive) return null

  const payments = listData?.tempBillingPayments?.payments || []
  const count = listData?.tempBillingPayments?.count || 0
  const totals = listData?.tempBillingPayments?.totals || EMPTY_TOTALS
  const invoicePercent = totals.totalInvoices
    ? Math.round((totals.settledInvoices / totals.totalInvoices) * 100)
    : 0
  const rangeLabel = count
    ? `${(currentPage - 1) * limit + 1} to ${Math.min(currentPage * limit, count)} of ${count}`
    : '0 results'

  return <div className="tbr-page">
    <header className="tbr-header">
      <div className="tbr-heading">
        <div className="tbr-eyebrow">Placements</div>
        <h1 className="tbr-title">{TEMP_BILLING_RECONCILIATION}</h1>
      </div>

      <div className="tbr-controls">
        {/* One activity report covers several ACHes, so this is the block the
            executive actually reports — every settled invoice across all of the
            month's payments, in one pasteable sheet.
            Placed FIRST, ahead of the filters: between two <select>s it was the
            same height and border as them and read as a third filter. */}
        {!!columnFilters?.activityReportSearch && (
          <PaidInvoicesModal
            activityReportMonth={columnFilters.activityReportSearch}
            label="Paid invoices for this activity report"
            withLabel
            className="tbr-btn is-outline"
          />
        )}

        <div className="tbr-search">
          <SearchIcon />
          <input
            className="tbr-search-input"
            type="text"
            placeholder="Search payment ref or file"
            value={columnFilters?.referenceIdSearch || ''}
            onChange={({ target: { value } }) => changeSearch(value)}
          />
        </div>

        {/* kan-112: find the file that carries an invoice. A second box rather
            than a third branch of the ref-or-file OR: an invoice number lives on
            the detail rows, not the payment, so it is a different question. */}
        <div className="tbr-search">
          <SearchIcon />
          <input
            className="tbr-search-input"
            type="text"
            title="Find the payment whose file carries this invoice number"
            placeholder="Search invoice #"
            value={columnFilters?.invoiceNumberSearch || ''}
            onChange={({ target: { value } }) => changeInvoiceSearch(value)}
          />
        </div>

        <select
          className="tbr-select"
          title="Filter by activity report"
          value={columnFilters?.activityReportSearch || ''}
          onChange={({ target: { value } }) => changeActivity(value)}
        >
          <option value="">All activity reports</option>
          {activityOptions.map((tag) => (
            <option key={tag} value={tag}>{moment(tag).format('MMM-YY')}</option>
          ))}
        </select>

        <select
          className="tbr-select"
          title="Which payments to show"
          value={status}
          onChange={({ target: { value } }) => {
            setFiltering(false)
            setCurrentPage(1)
            setStatus(value as TempBillingPaymentStatus)
          }}
        >
          {PAYMENT_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
    </header>

    <div className="tbr-summary">
      <div className="tbr-summary-card">
        {/* The card counts whatever the status filter left, so it must not claim
            "Open" while CLOSED or ALL is selected. */}
        <div className="tbr-summary-label">{PAYMENT_COUNT_LABEL[status]}</div>
        <div className="tbr-summary-value">{count}</div>
        <div className="tbr-summary-sub">{totals.stampedCount} stamped</div>
      </div>
      <div className="tbr-summary-card">
        <div className="tbr-summary-label">Unreconciled Amount</div>
        <div className="tbr-summary-value">{moneyFormatter(totals.unreconciledAmount || 0)}</div>
        <div className="tbr-summary-sub">across {count} files</div>
      </div>
      <div className="tbr-summary-card">
        <div className="tbr-summary-label">Settled Invoices</div>
        <div className="tbr-summary-value">
          {totals.settledInvoices} of {totals.totalInvoices}
        </div>
        <div className="tbr-summary-sub">{invoicePercent}% complete</div>
      </div>
      <div className="tbr-summary-card">
        <div className="tbr-summary-label">Unresolved Orphans</div>
        <div className="tbr-summary-value is-danger">{totals.orphanCount}</div>
        <div className="tbr-summary-sub">on {totals.filesWithOrphans} files</div>
      </div>
      {/* Parked, unexplained differences across the filtered payments. The
          headline money is the suspense BALANCE — the net difference (payment
          profit minus weekly activity) parked, not the invoices' whole profit,
          which is shown separately so the two are never confused. */}
      <div className="tbr-summary-card">
        <div className="tbr-summary-label">Suspense</div>
        <div className={`tbr-summary-value${totals.suspenseInvoices ? ' is-warn' : ''}`}>
          {totals.suspenseInvoices}
        </div>
        <div className="tbr-summary-sub">
          {moneyFormatter(totals.suspenseDifference || 0)} unexplained on {totals.filesWithSuspense} files
        </div>
        {!!totals.suspenseInvoices && (
          <div className="tbr-summary-sub">
            {moneyFormatter(totals.suspenseAmount || 0)} gross profit on those invoices
          </div>
        )}
      </div>
    </div>

    <div className="tbr-card">
      <div className="tbr-scroll">
        <div className="tbr-head">
          {COLUMNS?.map(({ key, label }) => (
            <button
              type="button"
              key={label}
              className="tbr-th"
              disabled={!key}
              onClick={() => key && changeSorting(key)}
            >
              <span>{label}</span>
              <span className="tbr-caret">{caretFor(key)}</span>
            </button>
          ))}
        </div>

        {listLoading && (
          <div className="tbr-loading">
            <Loader loaderClass="sm-height " />
          </div>
        )}

        {!listLoading && !payments?.length && (
          <div className="tbr-empty">No payments match the current filters.</div>
        )}

        {!listLoading && payments?.map((item: ITempBillingPayment) => paymentRow(item))}
      </div>

      <div className="tbr-footer">
        <span className="tbr-range">{rangeLabel}</span>

        <div className="tbr-footer-right">
          {!listLoading && !!payments?.length && (
            <Pagination
              onPageChange={(pageClicked: number) => {
                setFiltering(false)
                setCurrentPage(pageClicked);
              }} currentPage={currentPage}
              pageCount={Math.ceil(count / limit)}
            />
          )}

          <label className="tbr-rows">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={({ target: { value } }) => {
                setFiltering(false)
                setCurrentPage(1);
                setLimit(parseInt(value));
              }}
            >
              {ROWS_PER_PAGE?.map((rows) => (
                <option key={rows} value={rows}>{rows}</option>
              ))}
            </select>
          </label>
        </div>
      </div>
    </div>

    <StampPaymentModal
      isOpen={!!stampPayment}
      toggle={() => setStampPayment(null)}
      payment={stampPayment}
      refetch={refetch}
    />
  </div>
}

export default AllTempBillingReconciliations
